import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Heart, MapPin } from 'lucide-react';
import { isAuthenticated, toggleEventLike, getUserLikedEvents, trackEventClick } from '../services/api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Cards.css';

const CATEGORY_COLORS = {
  'Musica':         { bg: '#7C3AED', text: '#fff' },
  'Sport':          { bg: '#10B981', text: '#fff' },
  'Cinema':         { bg: '#3B82F6', text: '#fff' },
  'Concerto':       { bg: '#EC4899', text: '#fff' },
  'Teatro':         { bg: '#F59E0B', text: '#fff' },
  'Arte & Teatro':  { bg: '#F59E0B', text: '#fff' },
  'Famiglia':       { bg: '#06B6D4', text: '#fff' },
  'Altro':          { bg: '#64748B', text: '#fff' },
  'Generale':       { bg: '#8B5CF6', text: '#fff' },
};

const MONTH_IT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

class Cards extends Component {
  constructor(props) {
    super(props);
    this.state = { likedEvents: [], isLoading: false };
  }

  componentDidMount() { this.fetchLikedEvents(); }

  fetchLikedEvents = async () => {
    if (!isAuthenticated()) return;
    try {
      this.setState({ isLoading: true });
      const data = await getUserLikedEvents();
      this.setState({ likedEvents: data.map(e => e.id), isLoading: false });
    } catch {
      const stored = localStorage.getItem('likedEvents');
      this.setState({ likedEvents: stored ? JSON.parse(stored) : [], isLoading: false });
    }
  };

  handleEventClick = async (eventId, eventSlug, e) => {
    e.preventDefault();
    try { await trackEventClick(eventId); } catch {}
    window.location.href = `/event/${eventSlug || eventId}`;
  };

  handleLikeToggle = async (eventId, e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isAuthenticated()) {
      toast.info('Accedi per aggiungere ai preferiti', { position: 'top-center', autoClose: 2500 });
      return;
    }
    const { likedEvents } = this.state;
    const liked = likedEvents.includes(eventId);
    const next = liked ? likedEvents.filter(id => id !== eventId) : [...likedEvents, eventId];
    this.setState({ likedEvents: next });
    localStorage.setItem('likedEvents', JSON.stringify(next));
    toast.success(liked ? 'Rimosso dai preferiti' : '❤️ Aggiunto ai preferiti',
      { position: 'bottom-right', autoClose: 2000 });
    try { await toggleEventLike(eventId); } catch {
      this.setState({ likedEvents });
    }
    if (this.props.onLikeToggle) this.props.onLikeToggle(eventId, !liked);
  };

  getCategory = (event) => {
    if (event.category) {
      if (typeof event.category === 'object' && event.category.name) return event.category.name;
      if (typeof event.category === 'string') return event.category;
    }
    return event.category_name || 'Generale';
  };

  getCategoryStyle = (name) => CATEGORY_COLORS[name] || CATEGORY_COLORS['Generale'];

  getImageUrl = (event) => event.image_url || null;

  formatDateBadge = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    return { day: d.getDate(), month: MONTH_IT[d.getMonth()] };
  };

  formatLocation = (loc) => {
    if (!loc) return null;
    const parts = loc.split(',');
    return parts.length > 1 ? parts.slice(-2).map(s => s.trim()).join(', ') : loc;
  };

  render() {
    const { events } = this.props;

    if (!events || events.length === 0) {
      return (
        <div className="empty-state">
          <div className="empty-icon">🎉</div>
          <h4>Nessun evento trovato</h4>
          <p>Prova a cambiare categoria o i criteri di ricerca</p>
        </div>
      );
    }

    return (
      <>
        <ToastContainer />
        <div className="events-grid">
          {events.map(event => {
            const categoryName = this.getCategory(event);
            const catStyle = this.getCategoryStyle(categoryName);
            const imageUrl = this.getImageUrl(event);
            const dateBadge = this.formatDateBadge(event.start_date);
            const location = this.formatLocation(event.location);
            const liked = this.state.likedEvents.includes(event.id);

            return (
              <Link
                key={event.id}
                to={`/event/${event.slug || event.id}`}
                className="text-decoration-none"
                onClick={(e) => this.handleEventClick(event.id, event.slug, e)}
                style={{ display: 'flex' }}
              >
                <div className="event-card w-100">
                  {/* Image */}
                  <div className="image-container">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        className="card-image"
                        alt={event.title}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                          e.target.parentNode.classList.add('no-image');
                        }}
                      />
                    ) : (
                      <div className="card-img-placeholder">🎪</div>
                    )}
                    <div className="image-gradient" />

                    {/* Date badge */}
                    {dateBadge && (
                      <div className="card-date-badge">
                        <div className="card-date-day">{dateBadge.day}</div>
                        <div className="card-date-month">{dateBadge.month}</div>
                      </div>
                    )}

                    {/* Category */}
                    <span
                      className="badge category-badge"
                      style={{ background: catStyle.bg, color: catStyle.text }}
                    >
                      {categoryName}
                    </span>

                    {/* Like */}
                    <button
                      className="like-button"
                      onClick={(e) => this.handleLikeToggle(event.id, e)}
                      aria-label={liked ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
                    >
                      {liked
                        ? <Heart size={17} fill="#EC4899" style={{ color: "#EC4899" }} />
                        : <Heart size={17} style={{ color: '#64748B' }} />
                      }
                    </button>
                  </div>

                  {/* Body */}
                  <div className="card-body-custom">
                    <h3 className="event-title">
                      {event.title || 'Evento senza titolo'}
                    </h3>
                    <div className="event-meta">
                      {location && (
                        <div className="event-meta-item">
                          <MapPin size={13} />
                          <span>{location}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Share bar */}
                  <div className="card-share-bar" onClick={e => e.preventDefault()}>
                    {navigator.share ? (
                      <button
                        className="card-share-btn fb"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.share({ title: event.title, url: window.location.origin + '/event/' + (event.slug || event.id) }); }}
                        title="Condividi"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                      </button>
                    ) : (
                      <a
                        className="card-share-btn fb"
                        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/event/' + (event.slug || event.id) + '/')}`}
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        title="Condividi su Facebook"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
                      </a>
                    )}
                    <a
                      className="card-share-btn wa"
                      href={`https://wa.me/?text=${encodeURIComponent(event.title + ' — ' + window.location.origin + '/event/' + (event.slug || event.id))}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      title="Condividi su WhatsApp"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.657 1.438 5.168L2 22l4.978-1.301A9.96 9.96 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a7.963 7.963 0 01-4.274-1.243l-.306-.183-3.169.829.851-3.08-.2-.317A7.962 7.962 0 014 12c0-4.418 3.582-8 8-8s8 3.582 8 8-3.582 8-8 8z"/></svg>
                    </a>
                    <a
                      className="card-share-btn tw"
                      href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(event.title)}&url=${encodeURIComponent(window.location.origin + '/event/' + (event.slug || event.id))}`}
                      target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      title="Condividi su X"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </a>
                    <button
                      className="card-share-btn cp"
                      onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigator.clipboard.writeText(window.location.origin + '/event/' + (event.slug || event.id)); toast.success('Link copiato!', { position: 'bottom-right', autoClose: 1500 }); }}
                      title="Copia link"
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                    </button>
                  </div>

                  {/* Footer */}
                  <div className="card-footer-custom">
                    {event.distance ? (
                      <span className="distance-chip">
                        📍 {Math.round(event.distance * 10) / 10} km
                      </span>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                        {dateBadge ? `${dateBadge.day} ${dateBadge.month}` : ''}
                      </span>
                    )}
                    <span className="view-btn">Scopri →</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </>
    );
  }
}

export default Cards;
